import { Menu, Transition } from "@headlessui/react";
import {
  DocumentMagnifyingGlassIcon,
  EllipsisVerticalIcon,
  GlobeAmericasIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import { NotificationContext } from "../../common/context/notification";
import { classNames } from "../../common/utils/classnames";
import {
  RmFieldDetailsFragment,
  useRmFieldMakeSearchableMutation,
} from "../../graphql/generated";
import { ZenDialog, ZenDialogState } from "../../common/components/zen-dialog";

const FieldActions: React.FC<{
  field?: RmFieldDetailsFragment;
}> = ({ field }) => {
  const { notifier } = React.useContext(NotificationContext);
  const [makeSearchableDialogIsOpen, setMakeSearchableDialogIsOpen] =
    React.useState(false);

  const [{ fetching: makeSearchableFetching }, makeSearchableMutation] =
    useRmFieldMakeSearchableMutation();

  const makeSearchableCallback = React.useCallback(async () => {
    await makeSearchableMutation({
      fieldId: field?.id ?? "",
    }).then(notifier.notifyGraphql("Field is now searchable"));
    setMakeSearchableDialogIsOpen(false);
  }, [field, makeSearchableMutation, setMakeSearchableDialogIsOpen]);

  return (
    <>
      <Menu
        as="div"
        className="relative inline-block text-left mt-4 sm:mt-0 z-50"
      >
        <div>
          <Menu.Button className="bg-gray-100 rounded-full flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500">
            <span className="sr-only">Open options</span>
            <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
          </Menu.Button>
        </div>

        <Transition
          as={React.Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 focus:outline-none">
            <div className="py-1">
              <Menu.Item disabled={!field || !field.canBeMadeSearchable}>
                {({ active, disabled }) => (
                  <div
                    className={classNames(
                      active
                        ? "bg-gray-100 text-gray-900 cursor-pointer"
                        : disabled
                        ? "text-gray-400"
                        : "text-gray-700",
                      "group flex items-center px-4 py-2 text-sm cursor-pointer"
                    )}
                    onClick={() => setMakeSearchableDialogIsOpen(true)}
                  >
                    <DocumentMagnifyingGlassIcon
                      className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                      aria-hidden="true"
                    />
                    Make searchable...
                  </div>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
      <ZenDialog
        show={makeSearchableDialogIsOpen}
        title="Make field searchable"
        icon={GlobeAmericasIcon}
        submit="Submit"
        onSubmit={() => makeSearchableCallback()}
        onCancel={() => setMakeSearchableDialogIsOpen(false)}
        state={
          makeSearchableFetching
            ? ZenDialogState.Submitting
            : ZenDialogState.Displaying
        }
      >
        <div className="mt-1 space-y-2">
          <p>
            Marking a field as searchable will make <i>all future updates</i>{" "}
            searchable. To make past entities searchable, extra work is
            required.
          </p>
          <p>
            This only applies to <i>this MLS</i>. To make a Rezi field
            searchable for all MLSes, you must change the Rezi definition in
            code.
          </p>
        </div>
      </ZenDialog>
    </>
  );
};

export default FieldActions;
